import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["search", "providersList", "selectedTitle", "status", "emptyState", "errorBox",
    "emailPanel", "emailHint", "emailControls", "emailList", "selectAll", "selectionCount", "analyzeButton",
    "refreshButton", "metaText", "presentationContainer", "presentationText", "reportModal", "reportTitle",
    "reportError", "downloadButton", "viewReportButton"]

  connect() {
    this.requestId = 0
    this.busy = false
    this.emails = []
  }

  disconnect() {
    this.requestId += 1
    this.abortController?.abort()
    this.downloadAbortController?.abort()
    this.closeReport()
  }

  filterProviders() {
    const query = this.searchTarget.value.trim().toLowerCase()
    this.providersListTarget.querySelectorAll('[data-provider-title]').forEach((item) => {
      item.classList.toggle('d-none', !item.dataset.providerTitle.toLowerCase().includes(query))
    })
  }

  selectProvider(event) {
    const button = event.currentTarget
    this.providerId = button.dataset.providerId
    this.selectedTitleTarget.textContent = button.dataset.providerTitle
    this.providersListTarget.querySelectorAll('[data-provider-id]').forEach((item) => {
      item.setAttribute('aria-pressed', String(item === button))
    })
    this.refreshEmails()
  }

  startRequest() {
    this.abortController?.abort()
    this.abortController = new AbortController()
    this.requestId += 1
    this.errorBoxTarget.classList.add('d-none')
    this.clearResults()
    return this.requestId
  }

  async refreshEmails() {
    if (!this.providerId) return
    const requestId = this.startRequest()
    this.selectionToken = null
    this.emails = []
    this.emptyStateTarget.classList.add('d-none')
    this.emailPanelTarget.classList.remove('d-none')
    this.emailListTarget.innerHTML = '<p class="p-3 text-muted" role="status">Loading recent emails...</p>'
    this.setBusy(true)
    this.setStatus('Loading emails', 'warning')
    try {
      const response = await fetch(`/provider_intelligence/${this.providerId}/emails`, {
        headers: { 'Accept': 'application/json' }, credentials: 'same-origin', signal: this.abortController.signal
      })
      if (!(response.headers.get('content-type') || '').includes('application/json')) {
        throw new Error('Your session may have expired. Please refresh and sign in again.')
      }
      const payload = await response.json()
      if (requestId !== this.requestId) return
      if (!response.ok) throw new Error(payload.error || 'Unable to load provider emails.')
      this.emails = payload.emails
      this.selectionToken = payload.selection_token
      this.renderEmails()
      this.setStatus(this.emails.length ? 'Choose emails' : 'No emails', 'secondary')
    } catch (error) {
      if (requestId !== this.requestId || error.name === 'AbortError') return
      this.emailListTarget.innerHTML = ''
      this.showError(error.message)
    } finally {
      if (requestId === this.requestId) this.setBusy(false)
    }
  }

  renderEmails() {
    this.emailHintTarget.textContent = `${this.emails.length} recent emails, newest first (up to 20). Only checked emails will be analyzed.`
    this.emailListTarget.innerHTML = this.emails.length ? this.emails.map((email, index) => `
      <label class="intel-email-row" for="intel-email-${index}">
        <input type="checkbox" id="intel-email-${index}" value="${this.escapeHtml(email.id)}" data-email-checkbox
               data-action="change->provider-intelligence#selectionChanged">
        <span class="intel-email-content">
          <strong>${this.escapeHtml(email.subject || '(No subject)')}</strong>
          <span class="small text-muted d-block">${this.escapeHtml(email.sender)} · ${this.escapeHtml(this.formatDate(email.created_at))}${email.has_attachments ? ' · Attachments' : ''}</span>
          <span class="intel-email-preview">${this.escapeHtml(email.body_preview || 'No preview available.')}</span>
        </span>
      </label>`).join('') : '<p class="p-3 mb-0 text-muted">No emails found for this provider.</p>'
    this.updateSelection()
  }

  get checkboxes() {
    return Array.from(this.emailListTarget.querySelectorAll('[data-email-checkbox]'))
  }

  get selectedIds() {
    return this.checkboxes.filter((checkbox) => checkbox.checked).map((checkbox) => checkbox.value)
  }

  toggleAll() {
    this.checkboxes.forEach((checkbox) => { checkbox.checked = this.selectAllTarget.checked })
    this.selectionChanged()
  }

  selectionChanged() {
    this.clearResults()
    this.errorBoxTarget.classList.add('d-none')
    this.setStatus('Choose emails', 'secondary')
    this.updateSelection()
  }

  updateSelection() {
    const count = this.selectedIds.length
    this.selectionCountTarget.textContent = `${count} of ${this.emails.length} selected`
    this.selectAllTarget.checked = count > 0 && count === this.emails.length
    this.selectAllTarget.indeterminate = count > 0 && count < this.emails.length
    this.selectAllTarget.disabled = this.busy || this.emails.length === 0
    this.analyzeButtonTarget.disabled = this.busy || count === 0
  }

  setBusy(busy) {
    this.busy = busy
    this.emailControlsTarget.disabled = busy
    this.refreshButtonTarget.disabled = busy
    this.updateSelection()
  }

  async analyze() {
    const ids = this.selectedIds
    if (this.busy || !ids.length) return
    const requestId = this.startRequest()
    this.setBusy(true)
    this.setStatus('Analyzing', 'warning')
    this.presentationContainerTarget.classList.remove('d-none')
    this.presentationTextTarget.innerHTML = `
      <div class="intel-loading-card card" aria-busy="true">
        <div class="card-body">
          <h3 class="h6 font-weight-bold">Analyzing selected research</h3>
          <div class="intel-loading-progress" role="progressbar" aria-label="Analysis in progress">
            <div class="intel-loading-progress-bar"></div>
          </div>
          <p class="mb-1 mt-3" role="status" data-analysis-progress>Reading selected emails...</p>
          <p class="small text-muted mb-0">This can take a little while, especially with PDF attachments.</p>
        </div>
      </div>`
    this.reportTitleTarget.textContent = this.selectedTitleTarget.textContent
    this.viewReportButtonTarget.classList.remove('d-none')
    this.openReport()
    try {
      const response = await fetch(`/provider_intelligence/${this.providerId}/presentation`, {
        method: 'POST', credentials: 'same-origin', signal: this.abortController.signal,
        headers: {
          'Accept': 'text/event-stream', 'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
        },
        body: JSON.stringify({ email_ids: ids, selection_token: this.selectionToken })
      })
      const payload = await this.readPresentationStream(response, requestId)
      if (requestId === this.requestId) this.renderPresentation(payload)
    } catch (error) {
      if (requestId === this.requestId && error.name !== 'AbortError') this.showError(error.message)
    } finally {
      if (requestId === this.requestId) this.setBusy(false)
    }
  }

  async readPresentationStream(response, requestId) {
    if (!response.ok || !(response.headers.get('content-type') || '').includes('text/event-stream') || !response.body) {
      if (response.status === 401 || (response.redirected && /\/(users\/sign_in|login|sign_in)(?:[/?#]|$)/.test(response.url || ''))) {
        throw new Error('Your session has expired. Please sign in again.')
      }
      if (response.status === 422) {
        throw new Error('This page has expired. Refresh it and try the analysis again.')
      }
      if ((response.headers.get('content-type') || '').includes('application/json')) {
        const payload = await response.json()
        throw new Error(payload.error || 'Unable to start analysis. Please try again.')
      }
      if ([502, 503, 504].includes(response.status)) {
        throw new Error('The analysis connection timed out or was interrupted by the server. Please try again.')
      }
      const status = response.status ? ` (HTTP ${response.status})` : ''
      throw new Error(`Unable to start analysis. The server returned an unexpected response${status}. Please try again.`)
    }
    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    try {
      while (true) {
        const { done, value } = await reader.read()
        buffer += decoder.decode(value || new Uint8Array(), { stream: !done })
        const events = buffer.split(/\r?\n\r?\n/)
        buffer = events.pop()
        for (const rawEvent of events) {
          const lines = rawEvent.split(/\r?\n/)
          const name = lines.find((line) => line.startsWith('event:'))?.slice(6).trim()
          const rawData = lines.filter((line) => line.startsWith('data:')).map((line) => line.slice(5).trim()).join('\n')
          if (!name || !rawData) continue
          const data = JSON.parse(rawData)
          if (name === 'status' && requestId === this.requestId) {
            const node = this.presentationTextTarget.querySelector('[data-analysis-progress]')
            if (node) node.textContent = data.message
          }
          if (name === 'error') throw new Error(data.message || 'Unable to generate analysis.')
          if (name === 'complete') return data.payload
        }
        if (done) break
      }
      throw new Error('Analysis ended before a result was received. Please try again.')
    } finally {
      await reader.cancel().catch(() => {})
      reader.releaseLock()
    }
  }

  renderPresentation(payload) {
    this.setStatus('Ready', 'success')
    this.reportToken = payload.report_token
    this.downloadButtonTarget.disabled = !this.reportToken
    this.viewReportButtonTarget.classList.remove('d-none')
    const summary = payload.email_summary
    this.metaTextTarget.textContent = `${summary.email_count} selected emails analyzed · ${summary.pdf_attachments_processed} PDFs processed. Analysis uses available email text and PDF excerpts.`
    this.metaTextTarget.classList.remove('d-none')
    const points = payload.selling_points.map((point, index) => `
      <article class="intel-selling-point">
        <h4>${index + 1}. ${this.escapeHtml(point.title)}</h4>
        <p class="intel-preserve-lines">${this.escapeHtml(point.analysis)}</p>
        <div class="intel-pitch"><strong>Selling pitch</strong><p class="mb-0 intel-preserve-lines">${this.escapeHtml(point.pitch)}</p></div>
        <div class="small mt-2">Supporting quotes: ${point.evidence_ids.map((id) => `<a href="#intel-evidence-${this.escapeHtml(id)}" class="mr-2">${this.escapeHtml(id)}</a>`).join('')}</div>
      </article>`).join('') || '<p class="mb-0">The selected emails do not support a clear selling point. Try selecting more substantive research.</p>'
    const evidence = payload.evidence.map((item) => `
      <figure class="intel-evidence" id="intel-evidence-${this.escapeHtml(item.id)}" tabindex="-1">
        <div class="small font-weight-bold mb-2">${this.escapeHtml(item.id)}</div>
        <blockquote class="intel-preserve-lines">“${this.escapeHtml(item.quote)}”</blockquote>
        <figcaption class="small text-muted">${this.sourceMetadata(item)}</figcaption>
      </figure>`).join('') || '<p class="mb-0 text-muted">No supporting quotes identified.</p>'
    const tickers = payload.company_tickers.map((item) => `
      <article class="intel-ticker">
        ${item.company ? `<strong>${this.escapeHtml(item.company)}</strong>` : ''}
        ${item.ticker ? `<span class="badge bg-secondary">${this.escapeHtml(item.ticker)}</span>` : '<span class="small text-muted ml-2">Ticker not mentioned</span>'}
        <p class="small mb-1 mt-2">“${this.escapeHtml(item.mention)}”</p>
        <div class="small text-muted">${this.sourceMetadata(item)}</div>
      </article>`).join('') || '<p class="mb-0 text-muted">No companies or company tickers found in the analyzed material.</p>'
    this.presentationTextTarget.innerHTML = this.section('1. Selling points', points, 'intel-card-booking') +
      this.section('2. Supporting quotes', evidence, 'intel-card-email') +
      this.section('3. Companies and tickers mentioned', tickers, 'intel-card-static')
  }

  section(title, content, className) {
    return `<section class="card intel-card ${className} mb-3"><div class="card-header"><h3 class="h5 mb-0">${title}</h3></div><div class="card-body">${content}</div></section>`
  }

  sourceMetadata(item) {
    return `<strong>${this.escapeHtml(item.subject || '(No subject)')}</strong><br>${this.escapeHtml(item.sender)} · ${this.escapeHtml(this.formatDate(item.received_at))}<br>Source: ${this.escapeHtml(item.source)}`
  }

  clearResults() {
    this.downloadAbortController?.abort()
    this.reportToken = null
    this.closeReport()
    this.viewReportButtonTarget.classList.add('d-none')
    this.downloadButtonTarget.disabled = true
    this.downloadButtonTarget.textContent = 'Download PDF'
    this.reportErrorTarget.classList.add('d-none')
    this.presentationContainerTarget.classList.add('d-none')
    this.presentationTextTarget.innerHTML = ''
    this.metaTextTarget.classList.add('d-none')
  }

  setStatus(text, tone) {
    this.statusTarget.textContent = text
    this.statusTarget.className = `badge bg-${tone}`
  }

  showError(message) {
    const reportWasOpen = this.reportModalTarget.open
    this.clearResults()
    this.setStatus('Error', 'danger')
    if (reportWasOpen) {
      this.reportErrorTarget.textContent = message
      this.reportErrorTarget.classList.remove('d-none')
      this.openReport()
    } else {
      this.errorBoxTarget.textContent = message
      this.errorBoxTarget.classList.remove('d-none')
      this.errorBoxTarget.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
    }
  }

  openReport() {
    if (!this.reportModalTarget.open) this.reportModalTarget.showModal()
    document.body.classList.add('intel-report-open')
  }

  closeReport() {
    if (this.reportModalTarget.open) this.reportModalTarget.close()
    this.reportClosed()
  }

  reportClosed() {
    // A close event may arrive after a new report has already opened.
    if (!this.reportModalTarget.open) document.body.classList.remove('intel-report-open')
  }

  backdropClose(event) {
    if (event.target === this.reportModalTarget) this.closeReport()
  }

  async downloadPdf() {
    if (!this.reportToken || this.downloadButtonTarget.disabled) return
    const token = this.reportToken
    this.downloadAbortController = new AbortController()
    this.downloadButtonTarget.disabled = true
    this.downloadButtonTarget.textContent = 'Preparing PDF...'
    this.reportErrorTarget.classList.add('d-none')
    try {
      const response = await fetch(`/provider_intelligence/${this.providerId}/report_pdf`, {
        method: 'POST', credentials: 'same-origin', signal: this.downloadAbortController.signal,
        headers: {
          'Accept': 'application/pdf', 'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
        },
        body: JSON.stringify({ report_token: token })
      })
      if (!response.ok || !(response.headers.get('content-type') || '').includes('application/pdf')) {
        if ((response.headers.get('content-type') || '').includes('application/json')) {
          const data = await response.json()
          throw new Error(data.error || 'Unable to download the report. Please try again.')
        }
        throw new Error('Unable to download the report. Refresh the page if your session has expired.')
      }
      const blob = await response.blob()
      if (this.reportToken !== token) return
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      const filename = this.reportTitleTarget.textContent.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'provider'
      link.href = url
      link.download = `${filename}-intelligence-report.pdf`
      document.body.appendChild(link)
      link.click()
      link.remove()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch (error) {
      if (this.reportToken !== token || error.name === 'AbortError') return
      this.reportErrorTarget.textContent = error.message
      this.reportErrorTarget.classList.remove('d-none')
    } finally {
      if (this.reportToken === token) {
        this.downloadButtonTarget.disabled = false
        this.downloadButtonTarget.textContent = 'Download PDF'
      }
    }
  }

  formatDate(value) {
    if (!value) return 'Date unavailable'
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString()
  }

  escapeHtml(text) {
    return String(text ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#039;')
  }
}
